import React from 'react';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { cn } from '@/lib/utils';

const SkeletonLoader = ({ className, variant = 'video' }) => {
  if (variant === 'video') {
    return (
      <Card className={cn("overflow-hidden animate-card-reveal", className)}>
        <CardHeader className="p-0">
          <div className="relative">
            <div className="skeleton-dark h-48 w-full" />
            {/* Shimmer effect overlay */}
            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent animate-shimmer" />
          </div>
        </CardHeader>
        
        <CardContent className="p-4 space-y-3">
          {/* Creator Avatar & Info */}
          <div className="flex items-start gap-3">
            <div className="skeleton-dark h-10 w-10 rounded-full" />
            
            <div className="flex-1 space-y-2">
              <div className="skeleton-dark h-4 w-3/4" />
              <div className="skeleton-dark h-3 w-1/2" />
            </div>
          </div>
          
          {/* Stats */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="skeleton-dark h-3 w-12" />
              <div className="skeleton-dark h-3 w-10" />
            </div>
            <div className="skeleton-dark h-6 w-16" />
          </div>
          
          {/* Tags */}
          <div className="flex items-center gap-2">
            <div className="skeleton-dark h-5 w-16 rounded-full" />
            <div className="skeleton-dark h-5 w-20 rounded-full" />
          </div>
        </CardContent>
      </Card>
    );
  }

  if (variant === 'text') {
    return (
      <div className={cn("space-y-2", className)}>
        <div className="skeleton-dark h-4 w-full" />
        <div className="skeleton-dark h-4 w-4/5" />
        <div className="skeleton-dark h-4 w-3/5" />
      </div>
    );
  }

  if (variant === 'hero') {
    return (
      <div className={cn("relative w-full overflow-hidden bg-background animate-fade-in-up", className)} style={{ height: '80vh', minHeight: '600px' }}>
        <div className="skeleton-dark absolute inset-0" />
        
        {/* Animated shimmer overlay */}
        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/5 to-transparent animate-shimmer" />
        
        {/* Content skeleton */}
        <div className="relative z-10 flex items-center h-full">
          <div className="container mx-auto px-4 lg:px-8">
            <div className="max-w-3xl space-y-8">
              {/* Badges */}
              <div className="flex items-center gap-3">
                <div className="skeleton-dark h-6 w-12 rounded" />
                <div className="skeleton-dark h-6 w-16 rounded" />
                <div className="skeleton-dark h-6 w-14 rounded" />
              </div>

              {/* Title */}
              <div className="space-y-4">
                <div className="skeleton-dark h-16 w-4/5" />
                <div className="flex items-center gap-2">
                  <div className="skeleton-dark h-6 w-20 rounded-full" />
                  <div className="skeleton-dark h-6 w-16 rounded-full" />
                  <div className="skeleton-dark h-6 w-24 rounded-full" />
                </div>
              </div>
              
              {/* Description */}
              <div className="space-y-2">
                <div className="skeleton-dark h-5 w-full" />
                <div className="skeleton-dark h-5 w-4/5" />
              </div>
              
              {/* Buttons */}
              <div className="flex flex-col sm:flex-row gap-4 pt-6">
                <div className="skeleton-dark h-14 w-40" />
                <div className="skeleton-dark h-14 w-32" />
                <div className="skeleton-dark h-14 w-14" />
              </div>
            </div>
          </div>
        </div>
        
        {/* Bottom indicators */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex items-center gap-2">
          <div className="skeleton-dark h-2 w-8 rounded-full" />
          <div className="skeleton-dark h-2 w-2 rounded-full" />
          <div className="skeleton-dark h-2 w-2 rounded-full" />
        </div>
      </div>
    );
  }

  // Default skeleton
  return (
    <div className={cn("skeleton-dark h-20 w-full", className)} />
  );
};

// Loading dots component for buttons and inline loading
const LoadingDots = ({ className, size = 'sm' }) => {
  const sizeClasses = {
    xs: 'w-1 h-1',
    sm: 'w-2 h-2',
    md: 'w-3 h-3',
    lg: 'w-4 h-4'
  };

  return (
    <div className={cn("flex items-center gap-1", className)}>
      {[0, 1, 2].map((i) => (
        <div
          key={i}
          className={cn(
            "bg-current rounded-full animate-loading-dots",
            sizeClasses[size]
          )}
          style={{ animationDelay: `${i * 0.15}s` }}
        />
      ))}
    </div>
  );
};

// Success notification component
const SuccessNotification = ({ message, show, onClose }) => {
  if (!show) return null;

  return (
    <div className="fixed top-4 right-4 z-50 animate-slide-in-right">
      <div className="bg-primary text-primary-foreground px-4 py-3 rounded-lg shadow-lg flex items-center gap-2">
        <span className="text-lg animate-pizza-rotate">🍕</span>
        <span>{message}</span>
        <Button 
          variant="ghost"
          size="icon"
          onClick={onClose}
          className="ml-2"
          aria-label="Close"
        >
          ×
        </Button>
      </div>
    </div>
  );
};

// Confetti component for celebrations
const Confetti = ({ show, colors = ['#00b37e', '#ff4d4d', '#ffa500'] }) => {
  if (!show) return null;

  return (
    <div className="fixed inset-0 pointer-events-none z-50">
      {Array.from({ length: 20 }).map((_, i) => (
        <div
          key={i}
          className="confetti-piece animate-confetti-fall"
          style={{
            left: `${Math.random() * 100}%`,
            backgroundColor: colors[Math.floor(Math.random() * colors.length)],
            animationDelay: `${Math.random() * 2}s`,
            animationDuration: `${3 + Math.random() * 2}s`
          }}
        />
      ))}
    </div>
  );
};

export { SkeletonLoader, LoadingDots, SuccessNotification, Confetti };
export default SkeletonLoader;
